import { isCompactElqr, isSbpKgsPhoneCopyExperiment, paymentFormAbtest, paymentMethodAbtest, sbpKgsPhoneClipboard } from '../lib/paymentFormExperiment';
import { resolvePaymentAnalytics, retainPaymentAnalytics } from '../lib/paymentAnalyticsContext';
import { usePaymentAnalytics } from '../hooks/usePaymentAnalytics';
import type { PaymentMessage } from '../lib/paymentTranslations';
import { PaymentLanguagePicker, PaymentLanguageProvider, paymentErrorMessage, usePaymentLanguage } from '../lib/paymentLanguage';
import { KgsBankSelect } from '../components/ui/kgs-bank-select';
import { bankDisplayName } from '../lib/kgsBanks';
import { usePaymentDeadline, usesLocalDeadline } from '../hooks/usePaymentDeadline';
import { usePaymentPageLifecycle } from '../hooks/usePaymentPageLifecycle';
import { extractElqrPayload, verifyElqrPayload } from '../lib/elqr';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ElqrBankOptions } from '../components/payments/ElqrBankOptions';
import { ElqrScreenshotQr } from '../components/payments/ElqrScreenshotQr';
import { detectBankPlatform, isAndroidWebView, isIosWebKit } from '../lib/bankApps';
import { PaymentCopyButton, PaymentCopyAnnouncement } from '../components/payments/PaymentCopyFeedback';
import { usePaymentCopyFeedback, PaymentCopyField } from '../hooks/usePaymentCopyFeedback';
import { TransferPaymentRequisites } from '../components/payments/TransferPaymentRequisites';
import { ThemeToggle } from '../components/ThemeToggle';
import { useTheme } from '../contexts/ThemeContext';
import '../components/payments/transfer-payment.css';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CreditCard, Phone, Building, User, FileText, CheckCircle, ArrowLeft, Upload, Loader2, X, Download, Info, AlertCircle } from 'lucide-react';
import { paymentsAPI, PaymentTransactionResponse, API_BASE_URL } from '../lib/api';
import type { RequisiteType } from '../lib/api';
import { KztBankOptions } from '../components/payments/KztBankOptions';
import { isKztBankPaymentEligible } from '../lib/kztBankApps';
import { TransferBankOptions, TransferBankRequisites } from '../components/payments/TransferBankOptions';
import { isKgsBankPaymentEligible } from '../lib/kgsBankApps';
import { formatAmount, getExchangeRate } from '../lib/utils';
import { CountdownTimer } from '../components/ui/countdown-timer';
import { PhoneInput } from '../components/ui/phone-input';
import { BankAutocomplete } from '../components/ui/bank-autocomplete';
import { CardInput } from '../components/ui/card-input';
import {
    ANALYTICS_EVENTS,
    AnalyticsProperties
} from '../lib/analytics';
import { QRCodeSVG } from 'qrcode.react';
import { getPaymentRedirectTarget } from '../lib/paymentRedirect';

interface TraderRequisites extends TransferBankRequisites {
    phone_number?: string;
    transfer_amount?: string | number | null;
    recipient_bank?: string;
    recipient_name?: string;
    recipient_card?: string;
    mobile_operator?: string;
    notes?: string;
    qr_url?: string;
    qr_image_url?: string;
    qr_payload?: string;
}

interface OrderDetails {
    order_id: string;
    from_currency: string;
    to_currency: string;
    from_amount: number;
    to_amount: number;
    order_type: string;
}

interface PaymentBrandColors {
    primary: string;
    primary_hover: string;
    primary_text: string;
    primary_soft: string;
    background: string;
    surface: string;
    toolbar_surface?: string;
    requisite_surface?: string;
    text: string;
    text_muted: string;
    border?: string;
    copy_background?: string;
    copy_text?: string;
    note_background?: string;
    note_text?: string;
    note_icon?: string;
    note_hint?: string;
}

interface PaymentData {
    form_variant?: string | null;
    abtest?: string | null;
    form_experiments?: Record<string, { form_variant?: string | null; abtest?: string | null }>;
    brand_theme?: {
        name: string;
        logo_url: string;
        logo_alt: string;
        colors: PaymentBrandColors;
        dark_colors?: PaymentBrandColors;
    } | null;
    details_hidden?: boolean;
    payment_id: string;
    transaction_id: string;
    user_id: string;
    amount: number;
    fiat_currency?: string;
    commission_refund?: boolean;
    trader_id?: string | null;
    trader_requisites: TraderRequisites;
    order_details: OrderDetails;
    status: string;
    created_at: string;
    environment?: string;
    requisite_type?: RequisiteType | null;
    can_cancel?: boolean;
    show_expiration_timer?: boolean;
    expires_at?: string;
    duration_seconds?: number;
    timing_policy?: string | null;
    server_time?: string;
    success_url?: string | null;
    pending_url?: string | null;
    fail_url?: string | null;
}

const isTransferDeposit = (data: PaymentData | null) =>
    data?.order_details?.order_type === 'deposit'
    && (['sbp', 'sbp_kgs', 'card'].includes(data.requisite_type || '')
        || data.requisite_type?.startsWith('rub_transgran_uzs_emission_') === true
        || data.requisite_type?.startsWith('kzt_transgran_') === true);
const isVietnamQrDeposit = (data: PaymentData | null) =>
    data?.order_details?.order_type === 'deposit'
    && ['qr_vietnam_sber', 'qr_vietnam_vtb'].includes(data.requisite_type || '');

const MOBILE_OPERATORS = ['МТС', 'Билайн', 'МегаФон', 't2', 'Yota', 'Ростелеком'];

const SHOW_RECEIPT_UPLOAD = false;

const decodeQrImage = async (imageUrl: string): Promise<string | null> => {
    const { default: jsQR } = await import('jsqr');
    const response = await fetch(imageUrl);
    if (!response.ok) return null;

    const bitmap = await createImageBitmap(await response.blob());
    try {
        const canvas = document.createElement('canvas');
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) return null;

        context.drawImage(bitmap, 0, 0);
        const pixels = context.getImageData(0, 0, canvas.width, canvas.height);
        const decoded = jsQR(pixels.data, pixels.width, pixels.height);
        return extractElqrPayload(decoded?.data);
    } finally {
        bitmap.close();
    }
};

const getPaymentAnalyticsProperties = (data: PaymentData): AnalyticsProperties => ({
    payment_id: data.payment_id,
    abtest: paymentFormAbtest(data),
    method_abtest: paymentMethodAbtest(data),
    transaction_id: data.transaction_id,
    customer_id: data.user_id,
    trader_id: data.trader_id,
    order_id: data.order_details?.order_id,
    order_type: data.order_details?.order_type,
    status: data.status,
    amount: data.amount,
    from_currency: data.order_details?.from_currency,
    to_currency: data.order_details?.to_currency,
    environment: data.environment || 'live',
    requisite_type: data.requisite_type,
    fiat_currency: data.fiat_currency,
});

const getRequisiteValue = (value?: string | number | null) => {
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    return '';
};

export const PaymentDetailsPage: React.FC = () => {
    const { paymentId } = useParams<{ paymentId: string }>();
    return <PaymentDetailsSession key={paymentId} />;
};

const PaymentDetailsSession: React.FC = () => {
    const [paymentData, setPaymentData] = useState<PaymentData | null>(null);
    const currency = paymentData?.fiat_currency || (paymentData?.order_details?.order_type === 'withdraw'
        ? paymentData?.order_details?.to_currency : paymentData?.order_details?.from_currency);
    const useTransferToolbar = (isTransferDeposit(paymentData) || isCompactElqr(paymentData) || isVietnamQrDeposit(paymentData)) && !paymentData?.details_hidden
        && !['completed', 'cancelled', 'expired', 'disputed', 'no_traders'].includes(paymentData?.status || '');
    return <PaymentLanguageProvider currency={currency} requisiteType={paymentData?.requisite_type} showSelector={!useTransferToolbar}>
        <PaymentDetailsContent paymentData={paymentData} setPaymentData={setPaymentData} />
    </PaymentLanguageProvider>;
};

const PaymentDetailsContent: React.FC<{
    paymentData: PaymentData | null;
    setPaymentData: React.Dispatch<React.SetStateAction<PaymentData | null>>;
}> = ({ paymentData, setPaymentData }) => {
    const trackAnalyticsEvent = usePaymentAnalytics();
    const { t, language } = usePaymentLanguage();
    const { theme: colorMode } = useTheme();
    const { paymentId } = useParams<{ paymentId: string }>();
    const previousStatusRef = useRef<string | null>(null);
    const openedPaymentRef = useRef<string>();
    const loadedPaymentRef = useRef(false);
    const viewedPaymentRef = useRef(false);
    const [elqrVerified, setElqrVerified] = useState(false);
    const hiddenPaymentRef = useRef<string | undefined>();
    const redirectStartedRef = useRef(false);
    const generatedQrRef = useRef<HTMLDivElement | null>(null);
    const standardSandboxForm = paymentData?.environment === 'sandbox'
        && paymentData.order_details?.order_type === 'deposit'
        && process.env.REACT_APP_SANDBOX_STANDARD_FORM === 'true';
    const [verifiedElqr, setVerifiedElqr] = useState<{ source?: string; image?: string; payload: string } | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<PaymentMessage | null>(null);
    const { copy: copyRequisite, feedback: copyFeedback, copiedField } = usePaymentCopyFeedback();

    // Состояние для загрузки чека
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<PaymentMessage | null>(null);
    const [uploadSuccess, setUploadSuccess] = useState(false);

    // Состояние для отслеживания статуса транзакции
    const [transactionStatus, setTransactionStatus] = useState<string>('assigned');
    const [isCheckingStatus, setIsCheckingStatus] = useState(false);
    const [currentTransaction, setCurrentTransaction] = useState<PaymentTransactionResponse | null>(null);
    const timingPolicy = currentTransaction?.timing_policy ?? paymentData?.timing_policy;
    const completesOnTraderConfirmation = timingPolicy === 'local_withdraw_24h_v1';
    const paymentDeadline = {
        expiresAt: currentTransaction?.expires_at ?? paymentData?.expires_at,
        assignedAt: currentTransaction?.assigned_at ?? paymentData?.created_at,
        durationSeconds: currentTransaction?.duration_seconds ?? paymentData?.duration_seconds,
        serverTime: currentTransaction?.server_time ?? paymentData?.server_time,
    };
    const { expired: deadlineExpired } = usePaymentDeadline(paymentDeadline);
    const actionsExpired = usesLocalDeadline(timingPolicy) && deadlineExpired;


    useEffect(() => {
        if (!paymentData || isLoading || error || viewedPaymentRef.current
            || paymentData.order_details?.order_type !== 'deposit'
            || paymentData.details_hidden || transactionStatus !== 'assigned' || actionsExpired) return;
        const elqrReady = paymentData.requisite_type === 'elqr' && elqrVerified
            && (paymentData.trader_requisites.qr_url || paymentData.trader_requisites.qr_image_url);
        const phoneReady = isSbpKgsPhoneCopyExperiment(paymentData) && paymentData.trader_requisites.phone_number;
        if (!elqrReady && !phoneReady) return;
        const expose = () => {
            if (document.visibilityState !== 'visible' || viewedPaymentRef.current) return;
            viewedPaymentRef.current = true;
            trackAnalyticsEvent(ANALYTICS_EVENTS.PAYMENT_FORM_VIEWED, getPaymentAnalyticsProperties(paymentData));
        };
        expose();
        document.addEventListener('visibilitychange', expose);
        return () => document.removeEventListener('visibilitychange', expose);
    }, [paymentData, elqrVerified, isLoading, error, transactionStatus, actionsExpired, trackAnalyticsEvent]);

    // Состояние для формы реквизитов пользователя (для вывода)
    const [userRequisites, setUserRequisites] = useState({
        phone_number: '',
        recipient_bank: '',
        recipient_name: '',
        recipient_card: '',
        mobile_operator: ''
    });
    const [mobileOperatorChoice, setMobileOperatorChoice] = useState('');
    const [isSubmittingRequisites, setIsSubmittingRequisites] = useState(false);
    const [requisitesError, setRequisitesError] = useState<PaymentMessage | null>(null);
    const [requisitesSuccess, setRequisitesSuccess] = useState(false);

    // Состояние для подтверждения получения средств (для вывода)
    const [isConfirmingReceipt, setIsConfirmingReceipt] = useState(false);
    const [confirmReceiptError, setConfirmReceiptError] = useState<PaymentMessage | null>(null);

    // Состояние для отмены платежа
    const [isCancelling, setIsCancelling] = useState(false);
    const [cancelError, setCancelError] = useState<PaymentMessage | null>(null);
    const [sandboxAction, setSandboxAction] = useState<'complete' | 'cancel' | null>(null);
    const [sandboxActionError, setSandboxActionError] = useState<PaymentMessage | null>(null);
    const fiatCurrency = paymentData?.fiat_currency
        || (paymentData?.order_details?.order_type === 'withdraw'
            ? paymentData?.order_details?.to_currency
            : paymentData?.order_details?.from_currency)
        || 'RUB';

    usePaymentPageLifecycle({
        ...(paymentData ? getPaymentAnalyticsProperties(paymentData) : { payment_id: paymentId }),
        status: paymentData ? transactionStatus : 'loading',
    });

    useEffect(() => paymentId ? retainPaymentAnalytics(paymentId) : undefined, [paymentId]);

    useEffect(() => {
        let cancelled = false;
        const loadPaymentData = async () => {
            try {
                setIsLoading(true);
                setError(null);

                if (!paymentId) {
                    throw new Error('ID платежа не указан');
                }

                // Deduplicate StrictMode effects, but count a new visit after
                // leaving this form and returning to the same payment.
                if (openedPaymentRef.current !== paymentId) {
                    openedPaymentRef.current = paymentId;
                    trackAnalyticsEvent(ANALYTICS_EVENTS.PAYMENT_LINK_OPENED, {
                        payment_id: paymentId,
                    });
                }

                // Загружаем данные с сервера по payment_id
                const response = await fetch(`${API_BASE_URL}/payments/payment/${paymentId}`);

                if (!response.ok) {
                    throw new Error(response.status === 429 ? 'Превышен лимит запросов. Попробуйте позже.'
                        : response.status === 404 ? 'Платеж не найден' : 'Ошибка при загрузке данных платежа');
                }

                const data = await response.json();
                if (cancelled) return;
                resolvePaymentAnalytics(paymentId, paymentFormAbtest(data), paymentMethodAbtest(data));
                setPaymentData(data);
                setTransactionStatus(data.status || 'assigned');

            } catch (err: any) {
                if (cancelled) return;
                if (paymentId) resolvePaymentAnalytics(paymentId, 'unknown');
                console.error('Ошибка при загрузке данных:', err);

                // Проверяем, является ли это ошибкой блокировки (429)
                if (err.response?.status === 429) {
                    const errorDetail = err.response?.data?.detail || 'Превышен лимит запросов. Попробуйте позже.';
                    setError(paymentErrorMessage(errorDetail, 'Превышен лимит запросов. Попробуйте позже.'));
                    trackAnalyticsEvent(ANALYTICS_EVENTS.PAYMENT_LINK_LOAD_FAILED, {
                        payment_id: paymentId || null,
                        error_message: errorDetail,
                    });
                } else {
                    const message = err.message || 'Ошибка при загрузке данных платежа';
                    setError(paymentErrorMessage(message, 'Ошибка при загрузке данных платежа'));
                    trackAnalyticsEvent(ANALYTICS_EVENTS.PAYMENT_LINK_LOAD_FAILED, {
                        payment_id: paymentId || null,
                        error_message: message,
                    });
                }
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        loadPaymentData();
        return () => { cancelled = true; };
    }, [paymentId, setPaymentData, trackAnalyticsEvent]);

    useEffect(() => {
        if (!paymentData || loadedPaymentRef.current) return;
        // The provider has now resolved the currency-specific language.
        loadedPaymentRef.current = true;
        trackAnalyticsEvent(ANALYTICS_EVENTS.PAYMENT_LINK_LOADED, getPaymentAnalyticsProperties(paymentData));
    }, [paymentData, trackAnalyticsEvent]);

    useEffect(() => {
        if (!paymentData) return;

        const previousStatus = previousStatusRef.current;
        if (previousStatus && previousStatus !== transactionStatus) {
            trackAnalyticsEvent(ANALYTICS_EVENTS.PAYMENT_STATUS_CHANGED, {
                ...getPaymentAnalyticsProperties(paymentData),
                previous_status: previousStatus,
                status: transactionStatus,
            });
        }

        previousStatusRef.current = transactionStatus;
    }, [transactionStatus, paymentData, trackAnalyticsEvent]);

    useEffect(() => {
        if (!paymentData || redirectStartedRef.current) return;
        let target: string | null | undefined;
        if (transactionStatus === 'completed') {
            target = paymentData.success_url;
        } else if (transactionStatus === 'cancelled' || transactionStatus === 'no_traders') {
            target = paymentData.fail_url;
        } else if (transactionStatus === 'receipt_uploaded'
            || (transactionStatus === 'confirmed' && !completesOnTraderConfirmation)) {
            target = paymentData.pending_url;
        }
        const redirectTarget = getPaymentRedirectTarget(
            target,
            window.location.href,
        );
        if (redirectTarget) {
            redirectStartedRef.current = true;
            window.location.assign(redirectTarget);
        }
    }, [transactionStatus, paymentData, completesOnTraderConfirmation]);

    // Проверяем статус транзакции при загрузке компонента
    useEffect(() => {
        if (paymentData && paymentData.transaction_id) {
            checkTransactionStatus();
        }
    }, [paymentData]);

    // Запускаем автоматический polling при изменении статуса на receipt_uploaded
    useEffect(() => {
        let interval: NodeJS.Timeout | null = null;

        if (['pending_requisites', 'assigned', 'receipt_uploaded', 'confirmed'].includes(transactionStatus)) {
            console.log('Запускаем polling для статуса:', transactionStatus);

            interval = setInterval(async () => {
                if (!paymentData?.transaction_id) return;

                try {
                    console.log('Проверяем статус транзакции...');
                    const response = await paymentsAPI.getPaymentStatus(paymentData.transaction_id);
                    console.log('Новый статус:', response.status);
                    // Bank links and requisites may become available after payment creation.
                    if (paymentData.requisite_type?.startsWith('rub_transgran_uzs_emission_') && response.status !== 'completed') {
                        const details = await fetch(`${API_BASE_URL}/payments/payment/${paymentData.payment_id}`);
                        if (details.ok) setPaymentData(await details.json());
                    }

                    // Обновляем только если данные изменились
                    setTransactionStatus(prevStatus => {
                        if (prevStatus === response.status) {
                            return prevStatus;
                        }
                        return response.status;
                    });

                    setCurrentTransaction(prevTransaction => {
                        const dataString = JSON.stringify(response);
                        const prevString = JSON.stringify(prevTransaction);

                        if (dataString === prevString) {
                            return prevTransaction;
                        }

                        return response;
                    });

                    // Останавливаем опрос если платеж завершен
                    if (response.status === 'completed') {
                        console.log('Платеж завершен, останавливаем polling');
                        if (interval) {
                            clearInterval(interval);
                            interval = null;
                        }
                    }
                } catch (err) {
                    console.error('Ошибка при проверке статуса:', err);
                }
            }, 3000); // Проверяем каждые 3 секунды


        }

        // Cleanup function
        return () => {
            if (interval) {
                console.log('Очищаем polling interval');
                clearInterval(interval);
            }
        };
    }, [transactionStatus, paymentData?.transaction_id]);

    useEffect(() => {
        const source = paymentData?.trader_requisites.qr_url;
        const image = paymentData?.trader_requisites.qr_image_url;
        setVerifiedElqr(null);
        setElqrVerified(false);
        if (paymentData?.requisite_type !== 'elqr') return;

        let cancelled = false;
        const verify = async () => {
            const directPayload = await verifyElqrPayload(source);
            const payload = directPayload || (image
                ? await verifyElqrPayload(await decodeQrImage(image) || undefined)
                : null);
            if (!cancelled && payload) setVerifiedElqr({ source, image, payload });
        };
        verify().catch((error) => {
            console.warn('Не удалось проверить ElQR:', error);
        }).finally(() => { if (!cancelled) setElqrVerified(true); });
        return () => { cancelled = true; };
    }, [paymentData?.requisite_type, paymentData?.trader_requisites.qr_image_url, paymentData?.trader_requisites.qr_url]);

    const trackPaymentAction = (action: string, properties: AnalyticsProperties = {}) => {
        trackAnalyticsEvent(ANALYTICS_EVENTS.PAYMENT_ACTION_CLICKED, {
            ...(paymentData ? getPaymentAnalyticsProperties(paymentData) : { payment_id: paymentId }),
            status: transactionStatus,
            action,
            ...properties,
        });
    };

    const copyToClipboard = async (text: string, fieldName: PaymentCopyField) => {
        const isKgsSbpPhone = fieldName === 'phone' && (
            paymentData?.requisite_type === 'sbp_kgs'
            || (paymentData?.requisite_type === 'sbp' && fiatCurrency === 'KGS')
        );
        const phoneCopy = isKgsSbpPhone ? sbpKgsPhoneClipboard(text, paymentData) : null;
        const copyProperties = { field_name: fieldName, ...(phoneCopy ? { phone_copy_format: phoneCopy.format } : {}) };
        trackPaymentAction('copy_requisite', copyProperties);
        const clipboardText = phoneCopy?.text ?? (fieldName === 'card' ? text.replace(/\s/g, '') : text);
        if (await copyRequisite(clipboardText, fieldName)) {
            if (paymentData) {
                trackAnalyticsEvent(ANALYTICS_EVENTS.PAYMENT_REQUISITE_COPIED, {
                    ...getPaymentAnalyticsProperties(paymentData),
                    ...copyProperties,
                });
            }
        }
    };

    const saveBlob = (blob: Blob, filename: string) => {
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(objectUrl);
    };

    const downloadGeneratedQr = () => {
        trackPaymentAction('download_qr', { qr_source: 'generated' });
        const svg = generatedQrRef.current?.querySelector('svg');
        if (!svg) return;

        const clone = svg.cloneNode(true) as SVGSVGElement;
        clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        const source = new XMLSerializer().serializeToString(clone);
        saveBlob(
            new Blob([source], { type: 'image/svg+xml;charset=utf-8' }),
            `qr-${paymentId || 'payment'}.svg`,
        );
    };

    const downloadExternalQr = async (imageUrl: string) => {
        trackPaymentAction('download_qr', { qr_source: 'image' });
        try {
            const response = await fetch(imageUrl);
            if (!response.ok) throw new Error('QR image download failed');
            const blob = await response.blob();
            const extension = blob.type === 'image/png'
                ? 'png'
                : blob.type === 'image/webp'
                    ? 'webp'
                    : blob.type === 'image/gif'
                        ? 'gif'
                        : 'jpg';
            saveBlob(blob, `qr-${paymentId || 'payment'}.${extension}`);
        } catch (downloadError) {
            console.error('Ошибка скачивания QR-кода:', downloadError);
            window.open(imageUrl, '_blank', 'noopener,noreferrer');
        }
    };

    // Обработчик выбора файла
    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            // Проверяем тип файла
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
            if (!allowedTypes.includes(file.type)) {
                setUploadError('Поддерживаются только файлы форматов: JPG, PNG, PDF');
                return;
            }

            // Проверяем размер файла (максимум 10 МБ)
            if (file.size > 10 * 1024 * 1024) {
                setUploadError('Файл слишком большой. Максимальный размер: 10 МБ');
                return;
            }

            setSelectedFile(file);
            setUploadError(null);
        }
    };

    // Функция для проверки статуса транзакции
    const checkTransactionStatus = async () => {
        if (!paymentData) return;

        try {
            setIsCheckingStatus(true);
            const response = await paymentsAPI.getPaymentStatus(paymentData.transaction_id);
            console.log('Проверили статус:', response.status);

            // Обновляем только если данные изменились
            setTransactionStatus(prevStatus => {
                if (prevStatus === response.status) {
                    return prevStatus;
                }
                return response.status;
            });

            setCurrentTransaction(prevTransaction => {
                const dataString = JSON.stringify(response);
                const prevString = JSON.stringify(prevTransaction);

                if (dataString === prevString) {
                    return prevTransaction;
                }

                return response;
            });
        } catch (err: any) {
            console.error('Ошибка при проверке статуса:', err);
        } finally {
            setIsCheckingStatus(false);
        }
    };

    // Обработчик загрузки чека
    const handleUploadReceipt = async () => {
        trackPaymentAction('confirm_transfer', { with_receipt_file: SHOW_RECEIPT_UPLOAD && Boolean(selectedFile) });
        if (!paymentData) {
            setUploadError('Данные платежа не загружены');
            return;
        }

        try {
            setIsUploading(true);
            setUploadError(null);

            if (SHOW_RECEIPT_UPLOAD && selectedFile) {
                console.log('Загружаем чек для транзакции:', paymentData.transaction_id);
                await paymentsAPI.uploadReceipt(paymentData.transaction_id, selectedFile);
                console.log('Чек успешно загружен');
            } else {
                console.log('Подтверждаем перевод без чека для транзакции:', paymentData.transaction_id);
                await paymentsAPI.confirmTransfer(paymentData.transaction_id);
                console.log('Перевод подтверждён без чека');
            }

            setUploadSuccess(true);
            setTransactionStatus('receipt_uploaded');
            trackAnalyticsEvent(ANALYTICS_EVENTS.PAYMENT_TRANSFER_CONFIRMED, {
                ...getPaymentAnalyticsProperties(paymentData),
                with_receipt_file: SHOW_RECEIPT_UPLOAD && Boolean(selectedFile),
            });

            // Проверяем статус сразу после подтверждения
            setTimeout(() => {
                console.log('Запускаем проверку статуса после подтверждения перевода');
                checkTransactionStatus();
            }, 500);

        } catch (err: any) {
            console.error('Ошибка подтверждения перевода:', err);
            const message = err.response?.data?.detail || 'Не удалось подтвердить перевод';
            trackAnalyticsEvent(ANALYTICS_EVENTS.PAYMENT_TRANSFER_CONFIRM_FAILED, {
                ...getPaymentAnalyticsProperties(paymentData),
                error_message: message,
            });
            setUploadError(paymentErrorMessage(message, 'Не удалось подтвердить перевод'));
        } finally {
            setIsUploading(false);
        }
    };

    // Функция отправки реквизитов пользователя
    const handleSubmitRequisites = async () => {
        trackPaymentAction('submit_requisites');
        try {
            setIsSubmittingRequisites(true);
            setRequisitesError(null);

            if (paymentData?.requisite_type === 'mobile_kgs') {
                if (!userRequisites.phone_number || !userRequisites.mobile_operator.trim() || !userRequisites.recipient_name.trim()) {
                    setRequisitesError('Заполните телефон, мобильного оператора и получателя');
                    return;
                }
            } else if (!userRequisites.phone_number && !userRequisites.recipient_card) {
                setRequisitesError('Заполните хотя бы телефон или номер карты');
                return;
            }

            console.log('Отправляем реквизиты:', userRequisites);

            await paymentsAPI.submitUserRequisites(
                paymentData!.transaction_id,
                userRequisites
            );

            setRequisitesSuccess(true);
            setTransactionStatus('assigned');
            trackAnalyticsEvent(ANALYTICS_EVENTS.PAYMENT_USER_REQUISITES_SUBMITTED, {
                ...getPaymentAnalyticsProperties(paymentData!),
                has_phone: Boolean(userRequisites.phone_number),
                has_card: Boolean(userRequisites.recipient_card),
                has_bank: Boolean(userRequisites.recipient_bank),
                has_name: Boolean(userRequisites.recipient_name),
                mobile_operator: userRequisites.mobile_operator || null,
            });

            console.log('✅ Реквизиты успешно отправлены');

            // Проверяем статус сразу после отправки реквизитов
            setTimeout(() => {
                console.log('Запускаем проверку статуса после отправки реквизитов');
                checkTransactionStatus();
            }, 500);

        } catch (err: any) {
            console.error('Ошибка отправки реквизитов:', err);
            const message = err.response?.data?.detail || 'Не удалось отправить реквизиты';
            if (paymentData) {
                trackAnalyticsEvent(ANALYTICS_EVENTS.PAYMENT_USER_REQUISITES_SUBMIT_FAILED, {
                    ...getPaymentAnalyticsProperties(paymentData),
                    error_message: message,
                });
            }
            setRequisitesError(paymentErrorMessage(message, 'Не удалось отправить реквизиты'));
        } finally {
            setIsSubmittingRequisites(false);
        }
    };

    const handleConfirmReceipt = async () => {
        trackPaymentAction('confirm_receipt');
        try {
            setIsConfirmingReceipt(true);
            setConfirmReceiptError(null);

            console.log('Подтверждаем получение средств для транзакции:', paymentData!.transaction_id);

            await paymentsAPI.confirmReceipt(paymentData!.transaction_id);

            setTransactionStatus('completed');
            trackAnalyticsEvent(ANALYTICS_EVENTS.PAYMENT_RECEIPT_CONFIRMED, {
                ...getPaymentAnalyticsProperties(paymentData!),
            });
            console.log('✅ Получение средств подтверждено');

        } catch (err: any) {
            console.error('Ошибка подтверждения получения:', err);
            const message = err.response?.data?.detail || 'Не удалось подтвердить получение средств';
            if (paymentData) {
                trackAnalyticsEvent(ANALYTICS_EVENTS.PAYMENT_RECEIPT_CONFIRM_FAILED, {
                    ...getPaymentAnalyticsProperties(paymentData),
                    error_message: message,
                });
            }
            setConfirmReceiptError(paymentErrorMessage(message, 'Не удалось подтвердить получение средств'));
        } finally {
            setIsConfirmingReceipt(false);
        }
    };

    // Функция для отмены платежа
    const handleCancelPayment = async () => {
        trackPaymentAction('cancel_payment');
        if (!window.confirm(t("Вы уверены, что хотите отменить этот платеж?"))) {
            return;
        }

        try {
            setIsCancelling(true);
            setCancelError(null);

            console.log('Отменяем платеж:', paymentData!.transaction_id);

            await paymentsAPI.userCancelPayment(paymentData!.transaction_id);

            setTransactionStatus('cancelled');
            trackAnalyticsEvent(ANALYTICS_EVENTS.PAYMENT_CANCELLED_BY_USER, {
                ...getPaymentAnalyticsProperties(paymentData!),
            });
            console.log('✅ Платеж отменен');
            await checkTransactionStatus();

        } catch (err: any) {
            console.error('Ошибка отмены платежа:', err);
            const message = err.response?.data?.detail || 'Не удалось отменить платеж';
            if (paymentData) {
                trackAnalyticsEvent(ANALYTICS_EVENTS.PAYMENT_CANCEL_FAILED, {
                    ...getPaymentAnalyticsProperties(paymentData),
                    error_message: message,
                });
            }
            setCancelError(paymentErrorMessage(message, 'Не удалось отменить платеж'));
        } finally {
            setIsCancelling(false);
        }
    };

    const handleSandboxAction = async (action: 'complete' | 'cancel') => {
        if (!paymentData || paymentData.environment !== 'sandbox') return;
        trackPaymentAction(action === 'complete' ? 'sandbox_complete' : 'sandbox_cancel');
        const confirmation = standardSandboxForm
            ? t("Вы уверены, что хотите отменить этот платеж?")
            : t(action === 'complete' ? 'Подтвердить sandbox платеж?' : 'Отменить sandbox платеж?');
        if ((!standardSandboxForm || action === 'cancel') && !window.confirm(confirmation)) {
            return;
        }

        try {
            setSandboxAction(action);
            setSandboxActionError(null);
            const response = action === 'complete'
                ? await paymentsAPI.completeSandboxPayment(paymentData.payment_id)
                : await paymentsAPI.cancelSandboxPayment(paymentData.payment_id);
            setTransactionStatus(response.status);
            setCurrentTransaction((current) => current ? { ...current, status: response.status } : current);
        } catch (err: any) {
            console.error('Sandbox payment update failed:', err);
            setSandboxActionError(
                paymentErrorMessage(err.response?.data?.detail, standardSandboxForm ? 'Не удалось изменить платеж' : 'Не удалось изменить sandbox платеж')
            );
        } finally {
            setSandboxAction(null);
        }
    };

    // Функция для получения читаемого статуса
    const getStatusText = (status: string) => {
        const isWithdrawal = paymentData?.order_details?.order_type === 'withdraw';

        switch (status) {
            case 'pending_requisites':
                return t("Ожидает ввода реквизитов");
            case 'assigned':
                return isWithdrawal ? t("Ожидается перевод средств") : t("Ожидает оплаты");
            case 'receipt_uploaded':
                return ['qr_vietnam_sber', 'qr_vietnam_vtb', 'sbp_qr', 'elqr'].includes(paymentData?.requisite_type || '')
                    ? t("Перевод подтвержден, ожидает проверки")
                    : t("Ожидает подтверждения платежа");
            case 'confirmed':
                return isWithdrawal ? t("Средства отправлены") : t("Платеж подтвержден");
            case 'completed':
                return isWithdrawal ? t("Вывод завершен") : t("Платеж завершен");
            case 'cancelled':
                return t("Отменен");
            case 'disputed':
                return t("Открыт спор");
            case 'no_traders':
                return t("Нет доступных трейдеров");
            default:
                return t("Неизвестный статус");
        }
    };

    // Функция для получения цвета статуса
    const getStatusColor = (status: string) => {
        switch (status) {
            case 'assigned':
                return 'text-orange-600 dark:text-orange-300 bg-orange-50 dark:bg-orange-950';
            case 'pending_requisites':
                return 'text-yellow-700 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-950';
            case 'receipt_uploaded':
                return 'text-blue-600 dark:text-blue-300 bg-blue-50 dark:bg-blue-950';
            case 'confirmed':
                return 'text-green-600 dark:text-green-300 bg-green-50 dark:bg-green-950';
            case 'completed':
                return 'text-green-600 dark:text-green-300 bg-green-50 dark:bg-green-950';
            case 'cancelled':
                return 'text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-950';
            case 'disputed':
                return 'text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-950';
            case 'no_traders':
                return 'text-gray-600 dark:text-slate-300 bg-gray-50 dark:bg-slate-900';
            default:
                return 'text-gray-600 dark:text-slate-300 bg-gray-50 dark:bg-slate-900';
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600 dark:text-slate-300">{t("Загружаем данные платежа...")}</p>
                </div>
            </div>
        );
    }

    if (error || !paymentData) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center py-8 px-4">
                <Card className="w-full max-w-md">
                    <CardContent className="pt-6">
                        <div className="text-center">
                            <div className="text-red-600 dark:text-red-300 mb-4">
                                <p className="text-lg font-semibold">{t("Ошибка")}</p>
                                <p className="mt-2">{t(error || "Данные платежа не найдены")}</p>
                            </div>
                            <Link to="/">
                                <Button variant="outline">
                                    <ArrowLeft className="mr-2 h-4 w-4" />
                                    {t("На главную")}
                                </Button>
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const detailsHidden = hiddenPaymentRef.current === paymentId || paymentData.details_hidden
        || ['completed', 'cancelled', 'expired', 'disputed', 'no_traders'].includes(transactionStatus)
        || Boolean(currentTransaction?.completed_at || currentTransaction?.cancelled_at);
    if (detailsHidden) {
        hiddenPaymentRef.current = paymentId;
        const message = transactionStatus === 'completed' ? 'Платеж завершен'
            : transactionStatus === 'disputed' ? 'Платеж на рассмотрении'
            : ['cancelled', 'expired', 'no_traders'].includes(transactionStatus) ? 'Платеж отменен'
            : 'Данные платежа недоступны';
        return (
            <main className="grid min-h-[100svh] place-items-center bg-gray-50 px-4 dark:bg-slate-900">
                <Card className="w-full max-w-md rounded-2xl">
                    <CardContent className="p-6 text-center">
                        <p role="status" className="text-lg font-semibold">{t(message)}</p>
                    </CardContent>
                </Card>
            </main>
        );
    }

    const phoneNumber = getRequisiteValue(paymentData.trader_requisites.phone_number);
    const recipientCard = getRequisiteValue(paymentData.trader_requisites.recipient_card);
    const transferAmount = getRequisiteValue(paymentData.trader_requisites.transfer_amount)
        || formatAmount(paymentData.amount);
    const elqrPayload = paymentData.requisite_type === 'elqr'
        ? (verifiedElqr?.source === paymentData.trader_requisites.qr_url
            && verifiedElqr?.image === paymentData.trader_requisites.qr_image_url
            ? verifiedElqr?.payload : null)
        : null;

    const compactElqr = isCompactElqr(paymentData);
    const isVietnamQr = isVietnamQrDeposit(paymentData);
    const bankPlatform = detectBankPlatform(typeof navigator === 'undefined' ? undefined : navigator);
    const screenshotElqr = paymentData.requisite_type === 'elqr'
        && (bankPlatform === 'android' || isIosWebKit(typeof navigator === 'undefined' ? undefined : navigator));
    const isDeposit = paymentData.order_details?.order_type === 'deposit';
    const transferDesign = isTransferDeposit(paymentData) || compactElqr || isVietnamQr;
    const brandTheme = isDeposit && paymentData.form_variant === 'merchant_branded' ? paymentData.brand_theme : null;
    const legacyLightColors: PaymentBrandColors | null = brandTheme?.name === 'Melbet' && !brandTheme.dark_colors
        ? {
            ...brandTheme.colors,
            background: '#f3f5f8', surface: '#ffffff', toolbar_surface: '#ffffff', requisite_surface: '#f2f4f7',
            text: '#17191d', text_muted: '#69717e', border: '#e2e6ec',
            primary_soft: '#fff3cd', copy_background: '#fff1c2', copy_text: '#805b00',
            note_background: '#fff3dd', note_text: '#77500b', note_icon: '#dc9700', note_hint: '#85652d',
        }
        : null;
    const brandColors = brandTheme
        ? (colorMode === 'dark' ? brandTheme.dark_colors ?? brandTheme.colors : legacyLightColors ?? brandTheme.colors)
        : null;
    const brandStyle = brandColors ? ({
        '--payment-brand-primary': brandColors.primary,
        '--payment-brand-primary-hover': brandColors.primary_hover,
        '--payment-brand-primary-text': brandColors.primary_text,
        '--payment-brand-primary-soft': brandColors.primary_soft,
        '--payment-brand-background': brandColors.background,
        '--payment-brand-surface': brandColors.surface,
        '--payment-brand-text': brandColors.text,
        '--payment-brand-muted': brandColors.text_muted,
        '--payment-brand-copy-background': brandColors.copy_background ?? brandColors.primary_soft,
        '--payment-brand-copy-text': brandColors.copy_text ?? brandColors.primary,
        '--payment-brand-note-background': brandColors.note_background ?? '#fff3dd',
        '--payment-brand-note-text': brandColors.note_text ?? '#77500b',
        '--payment-brand-note-icon': brandColors.note_icon ?? '#dc9700',
        '--payment-brand-note-hint': brandColors.note_hint ?? '#85652d',
        '--transfer-bg': brandColors.background,
        '--transfer-surface': brandColors.surface,
        '--transfer-toolbar-surface': brandColors.toolbar_surface ?? brandColors.surface,
        '--transfer-inset': brandColors.requisite_surface ?? brandColors.surface,
        '--transfer-text': brandColors.text,
        '--transfer-muted': brandColors.text_muted,
        '--transfer-line': brandColors.border ?? '#e2e6ec',
    } as React.CSSProperties) : undefined;
    const isSbpTransfer = ['sbp', 'sbp_kgs'].includes(paymentData.requisite_type || '');
    const isKztTransfer = fiatCurrency === 'KZT'
        || paymentData.requisite_type?.startsWith('kzt_transgran_') === true;
    const internationalTransferBank = ({
        rub_transgran_uzs_emission_tbank: 'Т-Банк',
        rub_transgran_uzs_emission_vtb: 'ВТБ',
        rub_transgran_uzs_emission_sber: 'Сбер',
    } as Record<string, string>)[paymentData.requisite_type || ''];
    const formatPaymentPageAmount = (amount: number | string) => {
        const rawAmount = String(amount);
        if (!/^-?\d+(?:\.\d+)?$/.test(rawAmount)) return rawAmount;

        const hasFractionalAmount = /\.\d*[1-9]/.test(rawAmount);
        return Number(rawAmount).toLocaleString(language, {
            minimumFractionDigits: hasFractionalAmount ? 2 : 0,
            maximumFractionDigits: 20,
        });
    };
    const displayTransferAmount = formatPaymentPageAmount(transferAmount);
    const kztRefundNote = isDeposit && isKztTransfer && paymentData.commission_refund
        ? t("Комиссию мы вернем и зачислим в ваш личный кабинет. Сделайте международный перевод на {amount} ₸", { amount: displayTransferAmount })
        : null;
    const showKztBanks = isKztBankPaymentEligible({
        orderType: paymentData.order_details.order_type,
        fiatCurrency,
        requisiteType: paymentData.requisite_type,
        status: transactionStatus,
        actionsExpired,
    });
    const showKgsBanks = isKgsBankPaymentEligible({
        orderType: paymentData.order_details.order_type,
        fiatCurrency,
        requisiteType: paymentData.requisite_type,
        status: transactionStatus,
        actionsExpired,
    }) && !isAndroidWebView(typeof navigator === 'undefined' ? undefined : navigator);
    const showBankApps = showKztBanks || showKgsBanks;
    const requisiteRowClassName = showBankApps
        ? 'flex items-center justify-between gap-3 p-3 sm:p-4'
        : 'flex items-center justify-between gap-2 p-2 sm:p-3 bg-gray-50 dark:bg-slate-900 rounded-lg';
    const canCancelDeposit = isDeposit
        && paymentData.environment !== 'sandbox'
        && paymentData.can_cancel !== false
        && !['completed', 'cancelled', 'receipt_uploaded'].includes(transactionStatus);
    const canCancelSandbox = isDeposit && paymentData.environment === 'sandbox'
        && !['completed', 'cancelled', 'no_traders'].includes(transactionStatus);
    const showTimer = paymentData.show_expiration_timer !== false
        && !(completesOnTraderConfirmation && transactionStatus === 'confirmed')
        && (usesLocalDeadline(timingPolicy)
            ? ['pending_requisites', 'assigned', 'receipt_uploaded', 'confirmed'].includes(transactionStatus)
            : transactionStatus === 'assigned')
        && (paymentDeadline.expiresAt || paymentDeadline.assignedAt);

    const elqrContent = <>
        {paymentData.requisite_type === 'elqr' && elqrPayload && (
            <ElqrBankOptions
                compact={compactElqr}
                platform={bankPlatform}
                payload={elqrPayload}
                amount={transferAmount}
                currency={fiatCurrency}
                source={paymentData.trader_requisites.qr_url}
                analyticsProperties={{ ...getPaymentAnalyticsProperties(paymentData), status: transactionStatus }}
            />
        )}
        {screenshotElqr && !elqrPayload && (paymentData.trader_requisites.qr_image_url
            ? <ElqrScreenshotQr compact={compactElqr} image={paymentData.trader_requisites.qr_image_url} amount={transferAmount} currency={fiatCurrency} />
            : paymentData.trader_requisites.qr_url
                ? <ElqrScreenshotQr compact={compactElqr} value={paymentData.trader_requisites.qr_url} amount={transferAmount} currency={fiatCurrency} />
                : null)}
        {!screenshotElqr && paymentData.requisite_type === 'elqr' && !elqrPayload && paymentData.trader_requisites.qr_image_url && (
            <div className="flex flex-col items-center gap-2 rounded-lg bg-white dark:bg-slate-800 p-2 sm:p-4">
                <img
                    src={paymentData.trader_requisites.qr_image_url}
                    alt={t("ElQR для оплаты")}
                    className="h-60 w-60 object-contain"
                />
                <p className="text-xs text-gray-500 dark:text-slate-400">{t("Отсканируйте QR-код в банковском приложении")}</p>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => downloadExternalQr(paymentData.trader_requisites.qr_image_url!)}
                >
                    <Download className="mr-2 h-4 w-4" />
                    {t("Скачать QR")}
                </Button>
            </div>
        )}
        {!screenshotElqr && paymentData.requisite_type === 'elqr' && !elqrPayload && !paymentData.trader_requisites.qr_image_url && paymentData.trader_requisites.qr_url && (
            <div className="flex flex-col items-center gap-2 rounded-lg bg-white dark:bg-slate-800 p-2 sm:p-4">
                <div ref={generatedQrRef}>
                    <QRCodeSVG
                        value={paymentData.trader_requisites.qr_url}
                        size={240}
                        level="M"
                        includeMargin
                    />
                </div>
                <p className="text-xs text-gray-500 dark:text-slate-400">{t("Отсканируйте QR-код в банковском приложении")}</p>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={downloadGeneratedQr}
                >
                    <Download className="mr-2 h-4 w-4" />
                    {t("Скачать QR")}
                </Button>
            </div>
        )}
    </>;

    const vietnamQrContent = isVietnamQr && paymentData.trader_requisites.qr_payload ? (
        <section className="vietnam-qr-payment elqr-bank-options-compact space-y-0" aria-label={t("QR-код для оплаты")}>
            <div className="vietnam-qr-code-shell mx-auto w-full max-w-[280px] rounded-xl bg-white p-0">
                <div ref={generatedQrRef}>
                    <QRCodeSVG value={paymentData.trader_requisites.qr_payload} size={560} level="M" marginSize={4}
                        role="img" aria-label={t("QR-код для оплаты")} style={{ display: 'block', width: '100%', height: 'auto' }} />
                </div>
            </div>
            <ol className="list-decimal space-y-2 pl-5 text-sm text-gray-600 dark:text-slate-300">
                <li>{t("Сделайте скриншот QR-кода")}</li>
                <li>{t(paymentData.requisite_type === 'qr_vietnam_sber'
                    ? "Откройте приложение СберБанка и выберите оплату по QR-коду."
                    : "Откройте приложение ВТБ и выберите оплату по QR-коду.")}</li>
                <li>{t("Загрузите скриншот из галереи или отсканируйте QR-код с другого устройства.")}</li>
                <li>{t("Проверьте получателя и сумму, затем подтвердите оплату.")}</li>
            </ol>
            <p className="vietnam-qr-retry-note" role="note">
                <span>{t("Если произошла ошибка, попробуйте ещё раз.")}</span>
                <span>{t("Может потребоваться до пяти попыток")}</span>
            </p>
        </section>
    ) : null;

    return (
        <div className={`${transferDesign ? 'transfer-payment-page' : `bg-gray-50 dark:bg-slate-900 px-4 ${isDeposit ? 'grid min-h-[100svh] items-center py-3 sm:py-8' : 'min-h-screen py-8'}`} ${isVietnamQr ? 'vietnam-qr-page' : ''} ${brandTheme ? 'payment-brand-theme' : ''}`} style={brandStyle}>
            {transferDesign && <div className="transfer-payment-toolbar">
                <ThemeToggle />
                <PaymentLanguagePicker segmented />
            </div>}
            <div className={transferDesign ? `transfer-payment-card${isVietnamQr ? ' transfer-payment-card-vietnam-qr' : ''}` : `${isDeposit ? 'w-full max-w-xl space-y-2 sm:space-y-3' : 'max-w-2xl space-y-6'} mx-auto`}>
                {brandTheme && (
                    <div className="payment-brand-header">
                        <img src={brandTheme.logo_url} alt={brandTheme.logo_alt} />
                    </div>
                )}
                {!isDeposit && (
                    <Link to="/" className="inline-flex">
                        <Button variant="outline" size="sm">
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            {t("Назад")}
                        </Button>
                    </Link>
                )}

                {/* Заголовок и отметка тестового платежа */}
                <div className={transferDesign ? 'transfer-payment-heading' : 'text-center'}>
                    {paymentData.environment === 'sandbox' && !standardSandboxForm && (
                        <div className="mb-3 inline-flex rounded-md border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950 px-3 py-1 text-sm font-medium text-emerald-700 dark:text-emerald-300">
                            {t("Sandbox платеж, реальные переводы не требуются")}
                        </div>
                    )}
                    {!transferDesign && (isDeposit ? (
                        <h1 className="pb-2 text-center text-3xl font-bold text-gray-900 dark:text-slate-100 sm:text-4xl">{t("Пополнение")}</h1>
                    ) : (
                        <>
                            <h1 className="text-3xl font-bold text-gray-900 dark:text-slate-100">{t("Реквизиты для вывода")}</h1>
                            <p className="mt-2 text-gray-600 dark:text-slate-300">{t("Укажите ваши реквизиты для получения средств")}</p>
                        </>
                    ))}
                </div>

                {/* Информация о платеже */}
                {transferDesign ? (
                    <div className="transfer-payment-amount" data-copy-field="amount" data-copied={copiedField === 'amount'}>
                        <p className="transfer-payment-amount-label">{t("Сумма к переводу")}</p>
                        <div className="transfer-payment-amount-value">
                            <strong>{displayTransferAmount}</strong>
                            <span>{fiatCurrency}</span>
                            <PaymentCopyButton field="amount" feedback={copyFeedback} copied={copiedField === 'amount'}
                                onClick={() => copyToClipboard(transferAmount, 'amount')} />
                        </div>
                        {actionsExpired && <p className="mt-3 text-sm text-red-600 dark:text-red-300">{t("Время истекло. Получаем итоговый статус платежа…")}</p>}
                    </div>
                ) : isDeposit ? (
                    actionsExpired && <p className="px-1 text-sm text-gray-600 dark:text-slate-300">{t("Время истекло. Получаем итоговый статус платежа…")}</p>
                ) : (
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <CreditCard className="h-5 w-5 text-blue-600 dark:text-blue-300" />
                                {t("Детали платежа")}
                            </CardTitle>
                            {/* Таймер для активных платежей */}
                            {showTimer && (
                                <div className="mt-4">
                                    <CountdownTimer {...paymentDeadline} onExpired={() => { void checkTransactionStatus(); }} />
                                    {actionsExpired && <p className="mt-2 text-sm text-gray-600 dark:text-slate-300">{t("Время истекло. Получаем итоговый статус платежа…")}</p>}
                                </div>
                            )}
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <span className="text-sm text-gray-600 dark:text-slate-300">{t("ID транзакции:")}</span>
                                    <p className="font-mono text-sm break-all">{paymentData.transaction_id}</p>
                                </div>
                                <div>
                                    <span className="text-sm text-gray-600 dark:text-slate-300">{t("ID пользователя:")}</span>
                                    <p className="font-mono text-sm">{paymentData.user_id}</p>
                                </div>
                                <div>
                                    <span className="text-sm text-gray-600 dark:text-slate-300">
                                        {paymentData.order_details?.order_type === 'deposit' ? t("Сумма пополнения:") : t("Сумма вывода:")}
                                    </span>
                                    <p className="font-semibold text-lg">
                                        {formatPaymentPageAmount(paymentData.order_details?.order_type === 'deposit' ? paymentData.amount : paymentData.order_details.to_amount)} {fiatCurrency}
                                    </p>
                                </div>
                            </div>

                            <div className="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-slate-700">
                                <span className="text-sm text-gray-600 dark:text-slate-300">{t("Статус:")}</span>
                                <div className="flex items-center gap-2">
                                    <p className={`font-medium px-2 py-1 rounded ${getStatusColor(transactionStatus)}`}>
                                        {getStatusText(transactionStatus)}
                                    </p>
                                    {isCheckingStatus && (
                                        <Loader2 className="h-4 w-4 animate-spin text-blue-600 dark:text-blue-300" />
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {paymentData.environment === 'sandbox' && !standardSandboxForm &&
                    !['completed', 'cancelled', 'no_traders'].includes(transactionStatus) && (
                        <Card className="border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950">
                            <CardHeader>
                                <CardTitle className="text-emerald-900 dark:text-emerald-200">{t("Управление sandbox платежом")}</CardTitle>
                                <CardDescription className="text-emerald-800 dark:text-emerald-200">
                                    {t("Эти действия доступны только для sandbox-транзакции и не выполняют реальный перевод.")}
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                {sandboxActionError && (
                                    <div className="rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950 p-3 text-sm text-red-700 dark:text-red-300">
                                        {sandboxActionError && t(sandboxActionError)}
                                    </div>
                                )}
                                <div className={`grid grid-cols-1 gap-3 ${!isDeposit ? 'sm:grid-cols-2' : ''}`}>
                                    <Button
                                        onClick={() => handleSandboxAction('complete')}
                                        disabled={sandboxAction !== null}
                                        className="bg-emerald-600 hover:bg-emerald-700"
                                    >
                                        {sandboxAction === 'complete' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                        {t("Подтвердить платеж")}
                                    </Button>
                                    {!isDeposit && (
                                        <Button
                                            variant="destructive"
                                            onClick={() => handleSandboxAction('cancel')}
                                            disabled={sandboxAction !== null}
                                        >
                                            {sandboxAction === 'cancel' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                            {t("Отменить платеж")}
                                        </Button>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                {/* Реквизиты трейдера - только для пополнения (deposit) */}
                {isDeposit && (transferDesign ? (
                    <>
                        {compactElqr && elqrContent}
                        {isVietnamQr && vietnamQrContent}
                        {(!compactElqr || recipientCard || paymentData.trader_requisites.recipient_name || paymentData.trader_requisites.recipient_bank) && <section>
                            <div className="transfer-requisites-heading">
                                <h3 className="transfer-requisites-title">{showBankApps ? t("Реквизиты для перевода") : t("Реквизиты")}</h3>
                                <div className="transfer-payment-status">
                                    <span role="status" className="transfer-status-text">
                                        <span className="transfer-status-label--desktop">{getStatusText(transactionStatus)}</span>
                                        <span className="transfer-status-label--mobile">
                                            {transactionStatus === 'assigned' && paymentData.order_details?.order_type !== 'withdraw'
                                                ? t("Ожидает")
                                                : getStatusText(transactionStatus)}
                                        </span>
                                    </span>
                                    {isCheckingStatus && <Loader2 className="h-3 w-3 animate-spin" />}
                                    {showTimer && <CountdownTimer {...paymentDeadline} compact onExpired={() => { void checkTransactionStatus(); }} />}
                                </div>
                            </div>
                            <TransferPaymentRequisites currency={fiatCurrency}
                                bank={fiatCurrency !== 'KZT' && !paymentData.requisite_type?.startsWith('rub_transgran_uzs_emission_') ? paymentData.trader_requisites.recipient_bank : undefined}
                                phone={compactElqr ? undefined : phoneNumber} card={recipientCard}
                                recipient={paymentData.trader_requisites.recipient_name}
                                copiedField={copiedField} feedback={copyFeedback} onCopy={copyToClipboard} />
                        </section>}
                        {paymentData.trader_requisites.notes && <div className="transfer-payment-note">
                            <FileText aria-hidden="true" />
                            <div>
                                <p className="font-semibold">{t("Важные замечания")}</p>
                                <p>{paymentData.trader_requisites.notes}</p>
                            </div>
                        </div>}
                    </>
                ) : (
                    <Card className="rounded-2xl">
                        <CardHeader className="p-4 pb-3 sm:p-5 sm:pb-3">
                            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                                <CardTitle className="flex items-center gap-2 text-base">
                                    <User className="h-5 w-5 shrink-0 text-green-600 dark:text-green-300" />
                                    {showBankApps ? t("Реквизиты для перевода") : t("Реквизиты")}
                                </CardTitle>
                                <div className="transfer-payment-status">
                                    <span role="status" className="transfer-status-text">
                                        <span className="transfer-status-label--desktop">{getStatusText(transactionStatus)}</span>
                                        <span className="transfer-status-label--mobile">
                                            {transactionStatus === 'assigned' && paymentData.order_details?.order_type !== 'withdraw'
                                                ? t("Ожидает")
                                                : getStatusText(transactionStatus)}
                                        </span>
                                    </span>
                                    {isCheckingStatus && <Loader2 className="h-3 w-3 animate-spin" />}
                                    {showTimer && <CountdownTimer {...paymentDeadline} compact onExpired={() => { void checkTransactionStatus(); }} />}
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-2 p-3 pt-0 sm:p-5 sm:pt-0">
                            {showKztBanks && (
                                <div className="mb-3 flex items-start gap-3 rounded-xl border border-amber-300 border-l-4 border-l-amber-500 bg-amber-100 p-4 text-amber-950 dark:border-amber-700 dark:border-l-amber-400 dark:bg-amber-950 dark:text-amber-100">
                                    <Info className="mt-0.5 h-5 w-5 shrink-0 text-amber-700 dark:text-amber-300" aria-hidden="true" />
                                    <p className="min-w-0 text-base font-medium leading-relaxed">
                                        {kztRefundNote || t("Выполните международный перевод на указанные реквизиты").split(t("международный перевод")).map((part, index) => (
                                            <React.Fragment key={index}>
                                                {index > 0 && <strong className="font-extrabold">{t("международный перевод")}</strong>}
                                                {part}
                                            </React.Fragment>
                                        ))}
                                    </p>
                                </div>
                            )}
                            {showKgsBanks && (
                                <div className="mb-3 flex items-start gap-2 rounded-xl border border-blue-200 bg-blue-50 p-3 text-blue-950 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-100">
                                    <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-600 dark:text-blue-300" aria-hidden="true" />
                                    <p className="min-w-0 text-sm leading-relaxed">
                                        {t("Скопируйте реквизиты и сумму, затем выполните перевод через приложение банка.")}
                                    </p>
                                </div>
                            )}
                            {['qr_vietnam_sber', 'qr_vietnam_vtb', 'sbp_qr'].includes(paymentData.requisite_type || '') && paymentData.trader_requisites.qr_payload && (
                                <div className="flex flex-col items-center gap-2 rounded-lg bg-white dark:bg-slate-800 p-2 sm:p-4">
                                    <div ref={generatedQrRef}>
                                        <QRCodeSVG
                                            value={paymentData.trader_requisites.qr_payload}
                                            size={240}
                                            level="M"
                                            includeMargin
                                        />
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-slate-400">{t("Отсканируйте QR-код в банковском приложении")}</p>
                                    <Button type="button" variant="outline" size="sm" onClick={downloadGeneratedQr}>
                                        <Download className="mr-2 h-4 w-4" />
                                        {t("Скачать QR")}
                                    </Button>
                                </div>
                            )}
                            {elqrContent}
                            <div className={showBankApps ? 'divide-y divide-gray-200 overflow-hidden rounded-xl border border-gray-200 dark:divide-slate-700 dark:border-slate-700' : 'space-y-2'}>
                                {/* Телефон */}
                                {paymentData.requisite_type !== 'elqr' && phoneNumber && (
                                    <div className={requisiteRowClassName} data-copy-field="phone" data-copied={copiedField === 'phone'}>
                                        <div className="flex min-w-0 items-center gap-2 sm:gap-3">
                                            <Phone className="h-5 w-5 shrink-0 text-gray-600 dark:text-slate-300" />
                                            <div className="min-w-0">
                                                <p className="text-xs text-gray-500 dark:text-slate-400">{t("Телефон")}</p>
                                                <p className="break-words text-sm font-semibold sm:text-base">{phoneNumber}</p>
                                            </div>
                                        </div>
                                        <PaymentCopyButton field="phone" feedback={copyFeedback} copied={copiedField === 'phone'}
                                            className="h-11 w-11 border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800"
                                            onClick={() => copyToClipboard(phoneNumber, 'phone')} />
                                    </div>
                                )}

                                {/* Банк */}
                                {fiatCurrency !== 'KZT' && !paymentData.requisite_type?.startsWith('kzt_') && !['mobile_kgs', 'mobile_tjs'].includes(paymentData.requisite_type || '') && paymentData.trader_requisites.recipient_bank && (
                                    <div className={requisiteRowClassName} data-copy-field="bank" data-copied={copiedField === 'bank'}>
                                        <div className="flex min-w-0 items-center gap-2 sm:gap-3">
                                            <Building className="h-5 w-5 shrink-0 text-gray-600 dark:text-slate-300" />
                                            <div className="min-w-0">
                                                <p className="text-xs text-gray-500 dark:text-slate-400">{t("Банк")}</p>
                                                <p className="break-words text-sm font-semibold sm:text-base">{bankDisplayName(paymentData.trader_requisites.recipient_bank)}</p>
                                            </div>
                                        </div>
                                        <PaymentCopyButton field="bank" feedback={copyFeedback} copied={copiedField === 'bank'}
                                            className="h-11 w-11 border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800"
                                            onClick={() => copyToClipboard(bankDisplayName(paymentData.trader_requisites.recipient_bank), 'bank')} />
                                    </div>
                                )}

                                {/* Номер карты */}
                                {!['mobile_kgs', 'mobile_tjs'].includes(paymentData.requisite_type || '') && recipientCard && (
                                    <div className={requisiteRowClassName} data-copy-field="card" data-copied={copiedField === 'card'}>
                                        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                                            <CreditCard className="h-5 w-5 shrink-0 text-gray-600 dark:text-slate-300 flex-shrink-0" />
                                            <div className="min-w-0">
                                                <p className="text-xs text-gray-500 dark:text-slate-400">{t("Номер карты")}</p>
                                                <p className="font-semibold font-mono text-sm sm:text-base break-all">{recipientCard}</p>
                                            </div>
                                        </div>
                                        <PaymentCopyButton field="card" feedback={copyFeedback} copied={copiedField === 'card'}
                                            className="h-11 w-11 border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800"
                                            onClick={() => copyToClipboard(recipientCard, 'card')} />
                                    </div>
                                )}

                                {/* Мобильный оператор */}
                                {['mobile_kgs', 'mobile_tjs'].includes(paymentData.requisite_type || '') && paymentData.trader_requisites.mobile_operator && (
                                    <div className={requisiteRowClassName} data-copy-field="mobile_operator" data-copied={copiedField === 'mobile_operator'}>
                                        <div className="flex min-w-0 items-center gap-2 sm:gap-3">
                                            <Building className="h-5 w-5 shrink-0 text-gray-600 dark:text-slate-300" />
                                            <div className="min-w-0">
                                                <p className="text-xs text-gray-500 dark:text-slate-400">{t("Мобильный оператор")}</p>
                                                <p className="break-words text-sm font-semibold sm:text-base">{paymentData.trader_requisites.mobile_operator}</p>
                                            </div>
                                        </div>
                                        <PaymentCopyButton field="mobile_operator" feedback={copyFeedback} copied={copiedField === 'mobile_operator'}
                                            className="h-11 w-11 border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800"
                                            onClick={() => copyToClipboard(paymentData.trader_requisites.mobile_operator!, 'mobile_operator')} />
                                    </div>
                                )}

                                {/* Получатель */}
                                {paymentData.trader_requisites.recipient_name && (
                                    <div className={requisiteRowClassName} data-copy-field="name" data-copied={copiedField === 'name'}>
                                        <div className="flex min-w-0 items-center gap-2 sm:gap-3">
                                            <User className="h-5 w-5 shrink-0 text-gray-600 dark:text-slate-300" />
                                            <div className="min-w-0">
                                                <p className="text-xs text-gray-500 dark:text-slate-400">{t("Получатель")}</p>
                                                <p className="break-words text-sm font-semibold sm:text-base">{paymentData.trader_requisites.recipient_name}</p>
                                            </div>
                                        </div>
                                        <PaymentCopyButton field="name" feedback={copyFeedback} copied={copiedField === 'name'}
                                            className="h-11 w-11 border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800"
                                            onClick={() => copyToClipboard(paymentData.trader_requisites.recipient_name!, 'name')} />
                                    </div>
                                )}

                                {/* Сумма перевода */}
                                <div data-copy-field="amount" data-copied={copiedField === 'amount'} className={showBankApps
                                    ? 'flex items-center justify-between gap-3 bg-gray-50 p-3 dark:bg-slate-900 sm:p-4'
                                    : 'flex items-center justify-between gap-2 p-2 sm:p-3 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800'}>
                                    <div className="flex min-w-0 items-center gap-2 sm:gap-3">
                                        <CreditCard className="h-5 w-5 shrink-0 text-blue-600 dark:text-blue-300" />
                                        <div className="min-w-0">
                                            <p className="text-sm text-blue-600 dark:text-blue-300">{t("Сумма к переводу")}</p>
                                            <p className="font-bold text-lg text-blue-800 dark:text-blue-200">
                                                {displayTransferAmount} {fiatCurrency}
                                            </p>
                                        </div>
                                    </div>
                                    <PaymentCopyButton field="amount" feedback={copyFeedback} copied={copiedField === 'amount'}
                                            className="h-11 w-11 border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800"
                                            onClick={() => copyToClipboard(transferAmount, 'amount')} />
                                </div>
                            </div>

                            {/* Примечания */}
                            {paymentData.trader_requisites.notes && (
                                <div className="p-3 bg-yellow-50 dark:bg-yellow-950 rounded-lg border border-yellow-200 dark:border-yellow-800">
                                    <div className="flex items-start gap-3">
                                        <FileText className="h-5 w-5 shrink-0 text-yellow-600 dark:text-yellow-300 mt-0.5" />
                                        <div className="min-w-0">
                                            <p className="text-sm text-yellow-600 dark:text-yellow-300 font-medium">{t("Важные замечания")}</p>
                                            <p className="text-sm text-gray-700 dark:text-slate-200 mt-1">{paymentData.trader_requisites.notes}</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {showKztBanks && (
                                <div className="!mt-4 border-t border-gray-200 pt-4 dark:border-slate-700">
                                    <KztBankOptions
                                        paymentId={paymentData.payment_id}
                                        transactionId={paymentData.transaction_id}
                                    />
                                </div>
                            )}
                        </CardContent>
                    </Card>
                ))}

                {/* Форма ввода реквизитов для вывода */}
                {transactionStatus === 'pending_requisites' && !requisitesSuccess ? (
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <User className="h-5 w-5 text-green-600 dark:text-green-300" />
                                {t("Укажите ваши реквизиты")}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {requisitesError && (
                                <div className="p-3 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md">
                                    {requisitesError && t(requisitesError)}
                                </div>
                            )}

                            {paymentData.requisite_type === 'mobile_kgs' ? (
                                <>
                                    <div className="space-y-2">
                                        <Label>{t("Номер телефона *")}</Label>
                                        <PhoneInput
                                            value={userRequisites.phone_number}
                                            onChange={(value) => setUserRequisites(prev => ({ ...prev, phone_number: value }))}
                                            disabled={actionsExpired || isSubmittingRequisites}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="mobile-operator">{t("Мобильный оператор *")}</Label>
                                        <select
                                            id="mobile-operator"
                                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                            value={mobileOperatorChoice}
                                            onChange={(event) => {
                                                const value = event.target.value;
                                                setMobileOperatorChoice(value);
                                                setUserRequisites(prev => ({ ...prev, mobile_operator: value === 'other' ? '' : value }));
                                            }}
                                            disabled={actionsExpired || isSubmittingRequisites}
                                        >
                                            <option value="">{t("Выберите оператора")}</option>
                                            {MOBILE_OPERATORS.map(operator => <option key={operator} value={operator}>{operator}</option>)}
                                            <option value="other">{t("Другой")}</option>
                                        </select>
                                        {mobileOperatorChoice === 'other' && (
                                            <Input
                                                value={userRequisites.mobile_operator}
                                                placeholder={t("Название оператора")}
                                                onChange={(event) => setUserRequisites(prev => ({ ...prev, mobile_operator: event.target.value }))}
                                                disabled={actionsExpired || isSubmittingRequisites}
                                            />
                                        )}
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="mobile-recipient">{t("Получатель *")}</Label>
                                        <Input
                                            id="mobile-recipient"
                                            value={userRequisites.recipient_name}
                                            placeholder={t("Получатель")}
                                            onChange={(event) => setUserRequisites(prev => ({ ...prev, recipient_name: event.target.value }))}
                                            disabled={actionsExpired || isSubmittingRequisites}
                                        />
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="space-y-2">
                                        <Label>{t("Номер телефона *")}</Label>
                                        <PhoneInput value={userRequisites.phone_number} onChange={(value) => setUserRequisites(prev => ({ ...prev, phone_number: value }))} disabled={actionsExpired || isSubmittingRequisites} />
                                        <p className="text-xs text-gray-500 dark:text-slate-400">{t("Номер телефона, привязанный к банковской карте")}</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>{t("Банк")}</Label>
                                        {paymentData.fiat_currency === 'KGS' ? <KgsBankSelect value={userRequisites.recipient_bank} onChange={value => setUserRequisites(prev => ({ ...prev, recipient_bank: value }))} disabled={actionsExpired || isSubmittingRequisites} /> : <BankAutocomplete value={userRequisites.recipient_bank} onChange={(value) => setUserRequisites(prev => ({ ...prev, recipient_bank: value }))} disabled={actionsExpired || isSubmittingRequisites} />}
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="user-name">{t("ФИО получателя")}</Label>
                                        <Input id="user-name" placeholder={t("Иванов Иван Иванович")} value={userRequisites.recipient_name} onChange={(event) => setUserRequisites(prev => ({ ...prev, recipient_name: event.target.value }))} disabled={actionsExpired || isSubmittingRequisites} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>{t("Номер карты *")}</Label>
                                        <CardInput value={userRequisites.recipient_card} onChange={(value) => setUserRequisites(prev => ({ ...prev, recipient_card: value }))} disabled={actionsExpired || isSubmittingRequisites} />
                                    </div>
                                </>
                            )}

                            <Button
                                onClick={handleSubmitRequisites}
                                disabled={actionsExpired || isSubmittingRequisites || (paymentData.requisite_type === 'mobile_kgs'
                                    ? (!userRequisites.phone_number || !userRequisites.mobile_operator.trim() || !userRequisites.recipient_name.trim())
                                    : (!userRequisites.phone_number && !userRequisites.recipient_card))}
                                className="w-full"
                                size="lg"
                            >
                                {isSubmittingRequisites ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                        {t("Отправляем...")}
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle className="h-4 w-4 mr-2" />
                                        {t("Подтвердить реквизиты")}
                                    </>
                                )}
                            </Button>
                        </CardContent>
                    </Card>
                ) : (transactionStatus === 'pending_requisites' && requisitesSuccess) ? (
                    <Card>
                        <CardContent className="pt-6">
                            <div className="text-center py-8">
                                <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-slate-100 mb-2">
                                    {t("Реквизиты отправлены!")}
                                </h3>
                                <p className="text-gray-600 dark:text-slate-300">
                                    {t("Ваши реквизиты отправлены и скоро будут обработаны")}
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                ) : (transactionStatus === 'assigned' && paymentData.order_details.order_type === 'withdraw' && requisitesSuccess) ? (
                    /* Ожидание для вывода после отправки реквизитов */
                    <Card className="border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950">
                        <CardContent className="pt-6">
                            <div className="text-center text-blue-800 dark:text-blue-200">
                                <Loader2 className="h-12 w-12 text-blue-600 dark:text-blue-300 mx-auto mb-4 animate-spin" />
                                <p className="font-semibold text-lg">{t("Обработка заявки на вывод")}</p>
                                <p className="mt-2 text-sm">
                                    {t("Статус автоматически обновится")}
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                ) : null}

                {isTransferDeposit(paymentData) && (transactionStatus === 'pending_requisites' ||
                    (transactionStatus === 'assigned' && !uploadSuccess && !requisitesSuccess)) && (
                    <div className="transfer-payment-note">
                        <AlertCircle aria-hidden="true" />
                        <div>
                            {isKztTransfer || paymentData.requisite_type?.startsWith('rub_transgran_uzs_emission_') ? <>
                                <p className="transfer-payment-note-title"><strong className="font-bold">{kztRefundNote || (internationalTransferBank
                                    ? t("Выполните международный перевод через {bank} на указанные реквизиты", { bank: internationalTransferBank })
                                    : t("Выполните международный перевод на указанные реквизиты"))}</strong></p>
                                {!kztRefundNote && <p>{t("Переведите ровно {amount} {currency} одним платежом", {
                                    amount: displayTransferAmount, currency: fiatCurrency,
                                })}</p>}
                            </> : <p className="transfer-payment-note-title">{t(isSbpTransfer
                                ? "Переведите ровно {amount} {currency} в указанный банк одним платежом"
                                : "Переведите ровно {amount} {currency} на указанную карту одним платежом", {
                                amount: displayTransferAmount, currency: fiatCurrency,
                            })}</p>}
                            {!isKztTransfer && fiatCurrency !== 'KGS' && (
                                <p className="transfer-payment-note-hint">{t("Другая сумма не зачислится автоматически")}</p>
                            )}
                        </div>
                    </div>
                )}

                {isDeposit && transactionStatus === 'assigned' && !uploadSuccess && !requisitesSuccess
                    && paymentData.requisite_type?.startsWith('rub_transgran_uzs_emission_')
                    && !actionsExpired && paymentData.trader_requisites.sender_bank_code && (
                    <TransferBankOptions requisites={paymentData.trader_requisites}
                        analyticsProperties={{ ...getPaymentAnalyticsProperties(paymentData), status: transactionStatus }} />
                )}

                {/* Загрузка чека или показ статуса */}
                {(paymentData.environment !== 'sandbox' || standardSandboxForm) && transactionStatus === 'assigned' && !uploadSuccess && paymentData.order_details.order_type !== 'withdraw' ? (
                    <div>
                        <div className="sr-only">
                            <CardTitle className="flex items-center gap-2">
                                <Upload className="h-5 w-5 text-blue-600 dark:text-blue-300" />
                                {t("Подтвердите перевод")}
                            </CardTitle>
                            <CardDescription>
                                {t("После перевода нажмите кнопку ниже.")}
                            </CardDescription>
                        </div>
                        <div className="space-y-3">
                            {SHOW_RECEIPT_UPLOAD && (
                                <div>
                                    <input
                                        type="file"
                                        id="receipt-upload"
                                        accept="image/jpeg,image/jpg,image/png,application/pdf"
                                        onChange={handleFileSelect}
                                        className="hidden"
                                    />
                                    <label
                                        htmlFor="receipt-upload"
                                        className="flex items-center justify-center w-full p-6 border-2 border-dashed border-gray-300 dark:border-slate-700 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950 transition-colors"
                                    >
                                        <div className="text-center">
                                            <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                                            <p className="text-sm text-gray-600 dark:text-slate-300">
                                                {selectedFile ? selectedFile.name : t("Прикрепить чек (необязательно)")}
                                            </p>
                                            <p className="text-xs text-gray-400 mt-1">
                                                {t("JPG, PNG или PDF (макс. 10 МБ)")}
                                            </p>
                                        </div>
                                    </label>
                                </div>
                            )}

                            {/* Ошибки загрузки */}
                            {uploadError && (
                                <div className="p-3 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md">
                                    {uploadError && t(uploadError)}
                                </div>
                            )}

                            {/* Кнопка подтверждения перевода */}
                            <Button
                                onClick={standardSandboxForm ? () => handleSandboxAction('complete') : handleUploadReceipt}
                                disabled={actionsExpired || isUploading || sandboxAction !== null}
                                className={`${transferDesign ? 'transfer-payment-primary w-full' : 'h-12 w-full rounded-xl'} ${brandTheme ? 'payment-brand-primary' : ''}`}
                                size="lg"
                            >
                                {isUploading || sandboxAction === 'complete' ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                        {SHOW_RECEIPT_UPLOAD && selectedFile ? t("Загружаем чек...") : t("Подтверждаем...")}
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle className="h-4 w-4 mr-2" />
                                        {t("Я перевёл деньги")}
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                ) : transactionStatus === 'receipt_uploaded' &&
                    paymentData!.order_details.order_type === 'deposit' &&
                    !['qr_vietnam_sber', 'qr_vietnam_vtb', 'sbp_qr', 'elqr'].includes(paymentData!.requisite_type || '') ? (
                    /* Чек загружен, ожидает подтверждения - для обычных депозитов */
                    <Card className="border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950">
                        <CardContent className="pt-6">
                            <div className="text-center text-blue-800 dark:text-blue-200">
                                <Upload className="h-12 w-12 text-blue-600 dark:text-blue-300 mx-auto mb-4" />
                                <p className="mt-2 text-sm">
                                    {t("Ожидаем подтверждения платежа.")}{' '}{usesLocalDeadline(timingPolicy) ? t("Ожидайте подтверждения до окончания времени сделки.") : t("Обычно это занимает 5-15 минут.")}
                                </p>
                                <p className="mt-1 text-xs text-blue-600 dark:text-blue-300">
                                    {t("Статус обновляется автоматически")}
                                </p>
                                {isCheckingStatus && (
                                    <div className="mt-3">
                                        <Loader2 className="h-5 w-5 animate-spin text-blue-600 dark:text-blue-300 mx-auto" />
                                        <p className="text-xs mt-1">{t("Проверяем статус...")}</p>
                                    </div>
                                )}
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                        trackPaymentAction('check_status');
                                        void checkTransactionStatus();
                                    }}
                                    disabled={isCheckingStatus}
                                    className="mt-4"
                                >
                                    {isCheckingStatus ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                            {t("Проверяем...")}
                                        </>
                                    ) : (
                                        t("Проверить статус сейчас")
                                    )}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                ) : transactionStatus === 'confirmed' ? (
                    /* Платеж подтвержден */
                    <Card className="border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950">
                        <CardContent className="pt-6">
                            <div className="text-center text-green-800 dark:text-green-200">
                                <CheckCircle className="h-12 w-12 text-green-600 dark:text-green-300 mx-auto mb-4" />
                                <p className="font-semibold text-lg">
                                    {paymentData!.order_details.order_type === 'withdraw'
                                        ? t("Средства отправлены!")
                                        : t("Платеж подтвержден!")}
                                </p>
                                <p className="mt-2 text-sm">
                                    {paymentData!.order_details.order_type === 'withdraw'
                                        ? (completesOnTraderConfirmation
                                            ? t("Отправка подтверждена. Завершаем операцию.")
                                            : t("Проверьте ваши реквизиты и подтвердите получение средств."))
                                        : t("Операция будет завершена в ближайшее время.")}
                                </p>

                                {/* Кнопка подтверждения получения для вывода */}
                                {!completesOnTraderConfirmation && paymentData!.environment !== 'sandbox' && paymentData!.order_details.order_type === 'withdraw' && (
                                    <>
                                        {confirmReceiptError && (
                                            <div className="mt-4 p-3 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md">
                                                {confirmReceiptError && t(confirmReceiptError)}
                                            </div>
                                        )}

                                        <Button
                                            onClick={handleConfirmReceipt}
                                            disabled={actionsExpired || isConfirmingReceipt}
                                            className="mt-6"
                                            size="lg"
                                        >
                                            {isConfirmingReceipt ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                                    {t("Подтверждаем...")}
                                                </>
                                            ) : (
                                                <>
                                                    {t("Подтвердить получение средств")}
                                                </>
                                            )}
                                        </Button>

                                        <p className="mt-3 text-xs text-gray-600 dark:text-slate-300">
                                            {(timingPolicy === 'local_10m_v1' || timingPolicy === 'cascade_10m_v1') ? t("Операция завершится автоматически по окончании времени сделки.") : t("Или операция завершится автоматически через 15 минут")}
                                        </p>
                                    </>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                ) : transactionStatus === 'completed' ? (
                    /* Платеж завершен */
                    <Card className="border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950">
                        <CardContent className="pt-6">
                            <div className="text-center text-green-800 dark:text-green-200">
                                <CheckCircle className="h-12 w-12 text-green-600 dark:text-green-300 mx-auto mb-4" />
                                <p className="font-semibold text-lg">{t("🎉 Платеж успешно завершен!")}</p>
                                <p className="mt-2 text-sm">
                                    {t("Все этапы операции выполнены. Спасибо за использование нашего сервиса!")}
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                ) : transactionStatus === 'cancelled' ? (
                    /* Платеж отменен */
                    <Card className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950">
                        <CardContent className="pt-6">
                            <div className="text-center text-red-800 dark:text-red-200">
                                <div className="h-12 w-12 text-red-600 dark:text-red-300 mx-auto mb-4 flex items-center justify-center">
                                    ❌
                                </div>
                                <p className="font-semibold text-lg">{t("Платеж отменен")}</p>
                                <p className="mt-2 text-sm">
                                    {currentTransaction?.cancellation_reason === "Платеж просрочен (15 минут)"
                                        ? t("Время на оплату истекло (15 минут). Попробуйте создать новый платеж.")
                                        : currentTransaction?.cancellation_reason === "Отменено пользователем"
                                            ? t("Вы отменили этот платеж.")
                                            : currentTransaction?.cancellation_reason === "Отменено трейдером"
                                                ? t("Платеж был отменен трейдером.")
                                                : t("Платеж был отменен.")
                                    }
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                ) : paymentData!.environment !== 'sandbox' &&
                    paymentData!.order_details.order_type === 'deposit' ? (
                    /* Чек успешно загружен (fallback для deposit) */
                    <Card className="border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950">
                        <CardContent className="pt-6">
                            <div className="text-center text-green-800 dark:text-green-200">
                                <CheckCircle className="h-12 w-12 text-green-600 dark:text-green-300 mx-auto mb-4" />
                                <p className="font-semibold text-lg">{t("Чек успешно загружен!")}</p>
                                <p className="mt-2 text-sm">
                                    {t("Скоро будет подтвержден платеж. Вы получите уведомление о завершении операции.")}
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                ) : null}


                {/* Предупреждение - показываем только если еще не отправили чек/реквизиты */}
                {(transactionStatus === 'pending_requisites' ||
                    (transactionStatus === 'assigned' && !uploadSuccess && !requisitesSuccess)) && (
                        isDeposit ? (
                            !transferDesign && <p className="px-2 text-center text-xs leading-relaxed text-gray-500 dark:text-slate-400">
                                {t("Переведите точную сумму, затем подтвердите оплату.")}
                            </p>
                        ) : (
                            <Card className="border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-950">
                                <CardContent className="pt-6">
                                    <div className="text-center text-orange-800 dark:text-orange-200">
                                        <p className="font-semibold">{t("⚠️ Внимание!")}</p>
                                        <p className="mt-2 text-sm">
                                            {paymentData?.order_details?.order_type === 'withdraw'
                                                ? t("Проверьте правильность указанных реквизитов. Средства будут переведены на указанные вами данные.")
                                                : SHOW_RECEIPT_UPLOAD
                                                    ? t("Переводите точную сумму. После перевода нажмите \"Я перевёл деньги\" и загрузите чек об оплате для подтверждения.")
                                                    : t("Переводите точную сумму. После перевода нажмите \"Я перевёл деньги\" для подтверждения.")
                                            }
                                        </p>
                                    </div>
                                </CardContent>
                            </Card>
                        )
                    )}

                {/* Отладочная информация (только в dev режиме) */}
                {!isDeposit && process.env.NODE_ENV === 'development' && (
                    <details className="text-xs text-gray-500 dark:text-slate-400">
                        <summary>{t("Отладочная информация")}</summary>
                        <pre className="mt-2 p-2 bg-gray-100 dark:bg-slate-800 rounded">
                            {JSON.stringify(paymentData, null, 2)}
                        </pre>
                    </details>
                )}
                {transferDesign && showKztBanks && <div className="transfer-payment-banks">
                    <KztBankOptions compact paymentId={paymentData.payment_id} transactionId={paymentData.transaction_id} />
                </div>}
                {(canCancelDeposit || canCancelSandbox) && (
                    <div className={transferDesign ? 'transfer-payment-cancel' : 'border-t border-gray-200 dark:border-slate-700 pt-1 text-center'}>
                        {(cancelError || sandboxActionError) && (
                            <p role="alert" className="mb-2 text-sm text-red-600 dark:text-red-300">{t(cancelError || sandboxActionError!)}</p>
                        )}
                        <Button
                            variant="ghost"
                            className="min-h-[44px] text-gray-500 dark:text-slate-400 hover:bg-red-50 dark:hover:bg-red-950 hover:text-red-600 dark:hover:text-red-300"
                            onClick={canCancelSandbox ? () => handleSandboxAction('cancel') : handleCancelPayment}
                            disabled={isCancelling || sandboxAction !== null}
                        >
                            {(isCancelling || sandboxAction === 'cancel')
                                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("Отменяем...")}</>
                                : t("Отменить платеж")}
                        </Button>
                    </div>
                )}
            </div>
            <PaymentCopyAnnouncement feedback={copyFeedback} />
        </div>
    );
};
